import {
  KeyboardEvent,
  PointerEvent,
  ReactNode,
  TransitionEvent,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import styled from '@emotion/styled';
import {
  clampPanelWidth,
  keyboardResize,
  writeStoredPanelWidth,
} from './panelWidth';
import {
  AI_MOTION_MS,
  AI_MOTION_SETTLE_SLACK_MS,
  panelTransitionInitial,
  panelTransitionReducer,
} from './motion';

const RESIZE_HANDLE_Z_INDEX = 10;

export const ResizeHandle = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 0;
  bottom: 0;
  left: -3px;
  width: 8px;
  z-index: ${RESIZE_HANDLE_Z_INDEX};
  cursor: col-resize;
  touch-action: none;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 3px;
    width: 2px;
    background: ${({ $active }) =>
      $active ? 'var(--color-border-active)' : 'transparent'};
    transition: background 0.1s;
  }

  &:hover::after,
  &:focus-visible::after {
    background: var(--color-border-active);
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }
`;

export const RAIL_INERT_PROPS = { inert: '' } as Record<string, unknown>;

export const usePanelTransition = (
  content: ReactNode | null,
  reducedMotion: boolean
) => {
  const wanted = Boolean(content);
  const [transition, dispatch] = useReducer(
    panelTransitionReducer,
    wanted,
    panelTransitionInitial
  );
  if (wanted !== transition.wanted) {
    dispatch({ type: wanted ? 'show' : 'hide', reducedMotion });
  }
  const [lastContent, setLastContent] = useState<ReactNode>(content);
  if (content && content !== lastContent) setLastContent(content);
  if (!content && !transition.mounted && lastContent) setLastContent(null);

  const awaitingFrame =
    transition.mounted && transition.wanted && !transition.visible;
  useEffect(() => {
    if (!awaitingFrame) return undefined;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => dispatch({ type: 'frame' }));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [awaitingFrame]);

  const exiting = transition.mounted && !transition.wanted;
  useEffect(() => {
    if (!exiting) return undefined;
    const timer = setTimeout(
      () => dispatch({ type: 'exitEnd' }),
      AI_MOTION_MS + AI_MOTION_SETTLE_SLACK_MS
    );
    return () => clearTimeout(timer);
  }, [exiting]);

  const onTransitionEnd = (event: TransitionEvent<HTMLDivElement>) => {
    if (
      event.target === event.currentTarget &&
      event.propertyName === 'transform'
    ) {
      dispatch({ type: 'exitEnd' });
    }
  };

  return {
    ...transition,
    content: content ?? lastContent,
    onTransitionEnd,
  };
};

interface Drag {
  pointerId: number;
  startX: number;
  startWidth: number;
  latest: number;
  frame: number;
}

export const usePanelResize = (
  getWidth: () => number,
  setWidth: (width: number) => void
) => {
  const [resizing, setResizing] = useState(false);
  const dragRef = useRef<Drag | null>(null);

  const cancel = useCallback(() => {
    const drag = dragRef.current;
    if (drag) cancelAnimationFrame(drag.frame);
    dragRef.current = null;
    setResizing(false);
  }, []);

  useEffect(
    () => () => {
      const drag = dragRef.current;
      if (drag) cancelAnimationFrame(drag.frame);
    },
    []
  );

  useEffect(() => {
    if (!resizing) return undefined;
    const { style } = document.body;
    const previous = style.userSelect;
    style.userSelect = 'none';
    return () => {
      style.userSelect = previous;
    };
  }, [resizing]);

  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    const width = getWidth();
    dragRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startWidth: width,
      latest: width,
      frame: 0,
    };
    setResizing(true);
  };

  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    drag.latest = clampPanelWidth(
      drag.startWidth + (drag.startX - event.clientX),
      window.innerWidth
    );
    if (drag.frame) return;
    drag.frame = requestAnimationFrame(() => {
      drag.frame = 0;
      setWidth(drag.latest);
    });
  };

  const endResize = (event: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    cancelAnimationFrame(drag.frame);
    dragRef.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setWidth(drag.latest);
    setResizing(false);
    writeStoredPanelWidth(drag.latest);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const next = keyboardResize(
      event.key,
      event.shiftKey,
      getWidth(),
      window.innerWidth
    );
    if (next === null) return;
    event.preventDefault();
    setWidth(next);
    writeStoredPanelWidth(next);
  };

  return {
    resizing,
    cancel,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: endResize,
      onPointerCancel: endResize,
      onLostPointerCapture: endResize,
      onKeyDown,
    },
  };
};
